import React, { useState, useEffect, useRef } from 'react';
import {
  Icon,
  Segment,
  Form,
  TextArea,
  Header,
  Container,
  Grid,
  Message,
} from 'semantic-ui-react';
import { coursePageSaveScenario, courseAdi12SubmitScenarios } from 'dsoapi';

const SavingIndicator = ({ saving, lastSaved }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        opacity: saving || lastSaved ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
        zIndex: 1000,
      }}
    >
      {saving ? (
        <>
          <Icon name="circle notched" loading />
          Saving...
        </>
      ) : (
        <>
          <Icon name="check circle" color="green" />
          All changes saved
        </>
      )}
    </div>
  );
};

const CoursePageSubmitScenario = ({
  courseData,
  setPageValid,
  showValidationInfo,
}) => {
  const [scenarios, setScenarios] = useState([]);
  const [debouncedScenarios, setDebouncedScenarios] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [breakSecondsLeft, setBreakSecondsLeft] = useState(0);
  const validationMessageRef = useRef(null);
  const breakMessageRef = useRef(null);
  const confirmSubmitCallback = useRef(null);

  useEffect(() => {
    if (courseData?.scenarioInfo?.scenarios) {
      setScenarios(
        courseData.scenarioInfo.scenarios.map((scenario) => ({
          ...scenario,
          studentAnswerText: scenario.studentAnswerText || '',
        }))
      );
    }
    if (courseData?.scenarioInfo?.breakSecondsLeft > 0) {
      setOnBreak(true);
      setBreakSecondsLeft(courseData?.scenarioInfo?.breakSecondsLeft);
    }
  }, [courseData]);

  useEffect(() => {
    let timer;
    if (onBreak && breakSecondsLeft > 0) {
      timer = setInterval(() => {
        setBreakSecondsLeft((prev) => {
          if (prev <= 1) {
            setOnBreak(false);
            window.location.reload();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [onBreak, breakSecondsLeft]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedScenarios(scenarios);
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [scenarios]);

  useEffect(() => {
    if (debouncedScenarios) {
      const saveScenarios = async () => {
        setSaving(true);
        try {
          const saveRequest = {
            scenarios: debouncedScenarios.map((scenario) => ({
              userScenarioId: scenario.userScenarioId,
              studentAnswerText: scenario.studentAnswerText,
            })),
          };
          await coursePageSaveScenario(courseData.hashLink, saveRequest);
          setLastSaved(true);
          setTimeout(() => setLastSaved(false), 2000);
        } catch (error) {
          console.error('Error saving scenarios:', error);
        } finally {
          setSaving(false);
        }
      };
      saveScenarios();
    }
  }, [debouncedScenarios, courseData?.hashLink]);

  useEffect(() => {
    setPageValid(() => async () => {
      // Wait for any in-progress save to complete
      if (saving) {
        // Wait a short time for the save to complete
        await new Promise((resolve) => setTimeout(resolve, 100));
        // If still saving, wait a bit longer
        if (saving) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      // Only do final save if scenarios have changed since last save
      if (
        scenarios.length > 0 &&
        JSON.stringify(scenarios) !== JSON.stringify(debouncedScenarios)
      ) {
        const saveRequest = {
          scenarios: scenarios.map((scenario) => ({
            userScenarioId: scenario.userScenarioId,
            studentAnswerText: scenario.studentAnswerText,
          })),
        };
        try {
          await coursePageSaveScenario(courseData.hashLink, saveRequest);
        } catch (error) {
          console.error('Error saving scenarios:', error);
          return false;
        }
      }
      const allScenariosHaveMinimumWords = scenarios.every(
        (scenario) => getWordCount(scenario.studentAnswerText) >= 50
      );
      if (!allScenariosHaveMinimumWords) {
        return false;
      }
      if (onBreak) {
        if (breakMessageRef.current) {
          breakMessageRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
        window.alert('You are on a state mandated 24 hour break.');
        return false;
      }
      // Show confirmation dialog and wait for user response
      return new Promise((resolve) => {
        const confirmed = window.confirm(
          courseData?.scenarioInfo?.adi12Half === 'First'
            ? 'You are about to submit your first half scenario answers to be graded.  There will be a 24 hour state mandated break before you can continue.  Are you sure you would like to submit?'
            : 'You are about to submit your second half scenario answers to be graded.  Are you sure you would like to submit?'
        );
        if (confirmed) {
          handleConfirm().then(() => resolve(false));
        } else {
          resolve(false);
        }
      });
    });
  }, [
    setPageValid,
    scenarios,
    debouncedScenarios,
    courseData?.hashLink,
    saving,
    onBreak,
  ]);

  useEffect(() => {
    if (showValidationInfo && validationMessageRef.current) {
      validationMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [showValidationInfo]);

  useEffect(() => {
    if (onBreak && breakMessageRef.current) {
      breakMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [onBreak]);

  const handleAnswerChange = (index, value) => {
    const updatedScenarios = [...scenarios];
    updatedScenarios[index] = {
      ...updatedScenarios[index],
      studentAnswerText: value,
    };
    setScenarios(updatedScenarios);
  };

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const renderScenario = (scenario, index) => {
    const wordCount = getWordCount(scenario.studentAnswerText);
    const hasMinimumWords = wordCount >= 50;

    return (
      <Segment key={scenario.userScenarioId} raised>
        <Header as="h3">{scenario.scenarioTopicLabel}</Header>
        <p>{scenario.scenarioText}</p>
        <Form>
          <TextArea
            placeholder="Enter your answer here..."
            value={scenario.studentAnswerText}
            onChange={(e) => handleAnswerChange(index, e.target.value)}
            rows={4}
            style={{
              resize: 'none',
              backgroundColor: courseData?.scenarioInfo?.disabled
                ? '#f5f5f5'
                : 'white',
              borderColor: courseData?.scenarioInfo?.disabled
                ? '#ddd'
                : undefined,
              color: courseData?.scenarioInfo?.disabled ? '#666' : undefined,
              cursor: courseData?.scenarioInfo?.disabled
                ? 'not-allowed'
                : 'text',
            }}
            disabled={courseData?.scenarioInfo?.disabled}
          />
        </Form>
        <div
          style={{
            marginTop: '1em',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5em',
          }}
        >
          <Icon
            name={hasMinimumWords ? 'check circle' : 'times circle'}
            color={hasMinimumWords ? 'green' : 'red'}
            style={{ paddingBottom: '20px' }}
          />
          <span>Word Count: {wordCount}</span>
        </div>

        {scenario.score !== null && (
          <Message positive style={{ marginTop: '1em' }}>
            <Message.Header>Score</Message.Header>
            <p>{scenario.score}/100</p>
          </Message>
        )}

        {scenario.instructorFeedback && (
          <Message info style={{ marginTop: '1em' }}>
            <Message.Header>Instructor Feedback</Message.Header>
            <p>{scenario.instructorFeedback}</p>
          </Message>
        )}
      </Segment>
    );
  };

  const handleConfirm = async () => {
    const result = await courseAdi12SubmitScenarios(courseData.hashLink);
    if (result.data.success) {
      setOnBreak(true);
      console.log('setOnBreak(true)');
      setBreakSecondsLeft(86400); // 24 hours in seconds (24 * 60 * 60)
    }
    if (confirmSubmitCallback.current) {
      confirmSubmitCallback.current(result.data.success);
      confirmSubmitCallback.current = null;
    }
  };

  return (
    <Container style={{ maxWidth: '800px', padding: '2em 0' }}>
      <SavingIndicator saving={saving} lastSaved={lastSaved} />
      <Header as="h2" textAlign="center" style={{ marginBottom: '1.5em' }}>
        {courseData?.title}
      </Header>
      <Grid>
        {scenarios.map((scenario, index) => (
          <Grid.Row key={scenario.userScenarioId}>
            <Grid.Column>{renderScenario(scenario, index)}</Grid.Column>
          </Grid.Row>
        ))}
      </Grid>
      {scenarios.every((scenario) => scenario.score === null) &&
        !scenarios.every((scenario) => scenario.instructorFeedback) && (
          <div style={{ marginTop: '1em' }}>
            <p>
              * The DMV requires all answers to be at least 50 words and focused
              on the question topic.
            </p>
          </div>
        )}
      {showValidationInfo &&
        !scenarios.every(
          (scenario) => getWordCount(scenario.studentAnswerText) >= 50
        ) && (
          <div ref={validationMessageRef}>
            <Message error style={{ marginTop: '1em' }}>
              <Message.Header>Scenarios are incomplete</Message.Header>
              <p>All scenario answers must be at least 50 words.</p>
            </Message>
          </div>
        )}
      {onBreak && (
        <div ref={breakMessageRef} style={{ marginTop: '1em' }}>
          <Message info style={{ marginTop: '1em' }}>
            <Message.Header>24 Hour Break</Message.Header>
            <p>
              You are on a 24 hour state mandated break before you can continue.
            </p>
            <p style={{ marginTop: '0.5em', fontSize: '0.9em', color: '#666' }}>
              {Math.floor(breakSecondsLeft / 3600)} hours and{' '}
              {Math.floor((breakSecondsLeft % 3600) / 60)} minutes remaining
            </p>
          </Message>
        </div>
      )}
    </Container>
  );
};

export default CoursePageSubmitScenario;
